/* xDomeComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Steve = require("@gluenetworks/steve_core").getInstance();
const Renderer = require("./xDomeRenderer");
const SyncWorkSummary = require("../veils/SyncWork/SyncSummary/SyncSummaryComponent");
const _ = require("lodash");

/**
 * Represents the main component responsible for managing xDome-related view and operations.
 * Extends EventEmitter to facilitate event-driven architecture.
 *
 * @class xDomeViewComponent
 * @extends {EventEmitter}
 *
 * @fires xDomeViewComponent#OrganizationDataChange
 * @fires xDomeViewComponent#DataUpdated
 * @fires xDomeViewComponent#ErrorInLinking
 * @fires xDomeViewComponent#ErrorInSyncing
 * @fires xDomeViewComponent#ErrorInSiteSyncing
 * @fires xDomeViewComponent#ConnectionFailed
 * @fires xDomeViewComponent#ConnectionSuccessful
 * @fires xDomeViewComponent#SavedWithGoodConnection
 */
class xDomeViewComponent extends EventEmitter {
    /**
     *
     * @param {UserSession} userSession - User session management object.
     * @param {UiUtils} uiUtils - Utility methods for UI interactions.
     * @param {CoreLogger} logger - Logger for system-level logging.
     * @param {Object} userFeedback - Feedback utilities for user-facing veils and messages.
     * @param {Object} OrganizationService - Service for organization-related details.
     * @param {Object} DataSyncConfigEntityDef - Configuration entity for data synchronization.
     * @param {Object} steveIo - Steve utilities.
     * @param {Object} DeviceEntity - Entity for device details.
     * @param {Object} Explorables - Hierarchical device-related data available to users.
     * @param {Object} storageFactory - Factory for creating storage instances.
     * @param {Object} ValueMap - Value maps.
     * @param {Object} Router - Router instance for navigation.
     * @param {Object} dataSyncConfigEntity - Entity for managing data sync configurations.
     * @param {Object} serviceConnectionTypeEntity - Entity for managing service connection types.
     * @param {Object} serviceConnectionEntity - Entity for service connection details.
     * @param {Object} CustomFieldsEntity - Entity for custom fields.
     * @param {Object} ServiceConnectionEntityDef - Service connection entity definition.
     * @param {Object} SiteEntity - Entity for site data.
     */
    constructor(
        userSession,
        uiUtils,
        logger,
        userFeedback,
        OrganizationService,
        DataSyncConfigEntityDef,
        steveIo,
        DeviceEntity,
        Explorables,
        storageFactory,
        ValueMap,
        Router,
        dataSyncConfigEntity,
        serviceConnectionEntities,
        CustomFieldsEntity,
        ServiceConnectionEntityDef,
        SiteEntity
    ) {
        super();
        this._userSession = userSession;
        this._logger = logger;
        this._muf = userFeedback;
        this._uiUtils = uiUtils;
        this._organizationService = OrganizationService;
        this._organizationData = {};
        this._DataSyncConfigEntityDef = DataSyncConfigEntityDef;
        this._serviceConnectionEntities = serviceConnectionEntities || [];
        this._hasConnections = !!this._serviceConnectionEntities.length;
        this._Explorables = Explorables;
        this._storageFactory = storageFactory;
        this._ValueMapEntity = ValueMap;
        this._router = Router;
        this._explorablesEntity = undefined;
        this._dataForSlickTable = undefined;
        this._dataForTree = [];
        this._selectedClass = "cmdb_ci";
        this._steveIo = steveIo;
        this._DeviceEntityDef = DeviceEntity;
        this._fileFactory = steveIo.fileFactory;
        this._xdDataSyncConfigEntity = dataSyncConfigEntity;
        this._isDnCLErrored = false;
        this._isDnCSErrored = false;
        this._classAttributes = undefined;
        this._nodePropData = {};
        this._dataForSlickTableSyncing = [];
        this._dataForSlickTableSyncing_XDomeToGlu = [];
        this._dataForSlickTableSiteSyncing = [];
        this._dataForSlickTableSiteSyncing_XdToGlu = [];
        this._D2CSIdx = 0;
        this._D2CLIdx = 0;
        this._cmdbConnectionFailed = false;
        this._noVendorMap = false;
        this._CustomFieldsEntityDef = CustomFieldsEntity;
        this._customFieldsWithInherited = {};
        this._deviceTypeBaseMap = undefined;
        this._vendorOsMap = [];
        this._vendorMap = [];
        this._osMap = [];
        this._sitePathMap = [];
        this._SiteEntityDef = SiteEntity;
        this._ServiceConnectionEntityDef = ServiceConnectionEntityDef;
        this._skuList = [];
        this._metaData = undefined;
        this._serviceConnectionEntity = this._getConnectionEntity();

        this._renderer = new Renderer(this, uiUtils);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        const shutdowns = [this._renderer.shutdown()];

        return Promise.all(shutdowns).then(
            () => {
                // Set all properties to undefined
                [
                    "_organizationData",
                    "_dataSyncConfig",
                    "_Explorables",
                    "_storageFactory",
                    "_explorablesEntity",
                    "_dataForSlickTable",
                    "_dataForTree",
                    "_selectedClass",
                    "_DeviceEntityDef",
                    "_fileFactory",
                    "_isDnCLErrored",
                    "_isDnCSErrored",
                    "_classAttributes",
                    "_renderer",
                    "_nodePropData",
                    "_deviceNodeTreeData",
                    "_dataForCiAttrDropdown",
                    "_dataForSlickTableSyncing",
                    "_selectedClassSyncing",
                    "_classesFiltered",
                    "_dataForTreeFiltered",
                    "errLinkingIds",
                    "_cmdbConnectionFailed",
                    "_noVendorMap",
                    "errSyncingIds",
                    "_CustomFieldsEntityDef",
                    "_customFieldsWithInherited",
                    "_deviceTypeBaseMap",
                    "_vendorOsMap",
                    "_osMap",
                    "_vendorMap",
                    "_sitePathMap",
                    "_SiteEntityDef",
                    "_ServiceConnectionEntityDef",
                    "_skuList",
                    "_metaData",
                    "_serviceConnectionEntity",
                    "_serviceConnectionEntities",
                ].forEach((prop) => {
                    this[prop] = undefined;
                });
            },
            (err) => {
                console.error("Error shutting down xDomeViewComponent: ", err);
            }
        );
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    get activeOrgName() {
        return this.getActiveOrgName();
    }

    getActiveOrgName() {
        return _.get(this._userSession, "activeOrganization.name");
    }

    async whenModelIsReady() {
        try {
            // Load device types first
            this._loadDeviceTypes();

            // Execute all loading promises in parallel
            await this._loadMetaData();

            await Promise.all([
                this.loadOrganizationData(),
                this._retrieveCustomFieldsEntity(),
                // this._loadVendorOsMap(),
                // this._loadVendorMap(),
                // this._loadOsMap(),
                // this._loadSitePathMap(),
                // this._loadSkuList(),
            ]);

            // Test CMDB connection after initial data loading
            this._cmdbConnectionFailed = !(await this._runTestConnectionWork(
                this._serviceConnectionEntity,
                true
            ));
        } catch (err) {
            this._logger.warn(err, "xDomeViewComponent.whenModelIsReady() error.");
        }
    }

    _getConnectionEntity(id) {
        const serviceConnectionId = id || this._xdDataSyncConfigEntity.serviceConnectionId;
        return this._serviceConnectionEntities.find((entity) => entity.id === serviceConnectionId);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /*******************************************/

    /*******************************************/
    /* START SLICK DATA METHODS                */
    /*******************************************/

    // Device And Ci Linking Data - Start

    //
    // Linking Helper Functions
    //

    // Return path from root to className
    _getPath(attrDefs, className) {
        let path = [];
        while (className) {
            path.unshift(className);
            className = attrDefs[className] && attrDefs[className].parent;
        }
        return path;
    }

    // Generate list of derived linking rules

    /**
     * Generate Device And Ci Linking Data
     * @param {*} selectedTreeNode
     */
    async _generateSlickDataForInstanceLinking(selectedTreeNode = undefined) {
        try {
            await this._loadSlickDropdownsData(selectedTreeNode);
            this._dataForSlickTable = [];
            this._dataForSlickTableDerived = [];
            const dataForLinkingCriteria =
                    this._xdDataSyncConfigEntity.getInstanceLinkingCriteria() || {},
                dataForCurrentClass = dataForLinkingCriteria[selectedTreeNode] || [],
                dataForDerivedClassRules = this._generateDerivedLinkingRules(
                    this._classAttributes,
                    dataForLinkingCriteria,
                    selectedTreeNode
                );

            dataForDerivedClassRules.forEach((item, idx) => {
                this._dataForSlickTableDerived.push({
                    solutions: this._nodePropData,
                    deviceNodeTreeData: this._deviceNodeTreeData,
                    ciAttributesDropDownData: this._dataForCiAttrDropdown,
                    ciAttributesData: this._classAttributes,
                    id: `id_${idx}`,
                    ignore: item["ignore"] || false,
                    gwPropPath: item["gwPropPath"],
                    gwPropType: this.findPathForDeviceProps(item["gwPropPath"], this._nodePropData),
                    cmdbAttr: item["cmdbAttr"],
                    cmdbClass: this._selectedClass,
                    sourceCmdbClass: item["cmdbClass"],
                    xdPropType: this.findType(this._dataForCiAttrDropdown, item["cmdbAttr"]),
                });
            });

            dataForCurrentClass.forEach((item, idx) => {
                if (item.ignorePlaceholder !== true) {
                    this._dataForSlickTable.push({
                        solutions: this._nodePropData,
                        deviceNodeTreeData: this._deviceNodeTreeData,
                        ciAttributesDropDownData: this._dataForCiAttrDropdown,
                        ciAttributesData: this._classAttributes,
                        id: `id_${idx}`,
                        ignore: item["ignore"] || false,
                        gwPropPath: item["gwPropPath"],
                        gwPropType: this.findPathForDeviceProps(
                            item["gwPropPath"],
                            this._nodePropData
                        ),
                        cmdbAttr: item["cmdbAttr"],
                        cmdbClass: this._selectedClass,
                        xdPropType: this.findType(this._dataForCiAttrDropdown, item["cmdbAttr"]),
                    });
                    //we should track the last used idx so we always use a unique higher value
                    this._D2CLIdx = idx;
                }
            });

            this.emit("RefreshDeviceAndCiLinkingGrid");
        } catch (err) {
            return Promise.reject(err);
        }
    }

    /**
     * Add Device And Ci Linking Row
     */
    addNewD2CLinkingRule() {
        const idx = Math.random() * 100;
        const firstDevicePropNode = this.findFirstNode(this._nodePropData);

        if (!_.isEmpty(this._dataForCiAttrDropdown)) {
            this._dataForSlickTable.push({
                solutions: this._nodePropData,
                deviceNodeTreeData: this._deviceNodeTreeData,
                ciAttributesDropDownData: this._dataForCiAttrDropdown,
                ciAttributesData: this._classAttributes,
                id: `id_${idx}`,
                ignore: _.get(firstDevicePropNode, "ignore", false),
                gwPropPath: _.get(firstDevicePropNode, "path", []),
                gwPropType: _.get(firstDevicePropNode, "dataType", ""),
                cmdbAttr: this._dataForCiAttrDropdown ? this._dataForCiAttrDropdown[0].value : "",
                xdPropType: this.findType(
                    this._dataForCiAttrDropdown,
                    this._dataForCiAttrDropdown ? this._dataForCiAttrDropdown[0].value : ""
                ),
                cmdbClass: this._selectedClass,
            });

            this.setInstanceLinking();

            this.emit("RefreshDeviceAndCiLinkingGrid");
        } else {
            const errObj = {
                errorCode: "CI_ATTRIBUTES_ERR",
                errorMsg: `No CI Attributes exist for the selected node. You will not be able to add any Instance Linking Criteria for the selected Node`,
                title: "Error",
            };
            return this._muf.displayError(errObj);
        }
    }
    // Device And Ci Linking Data - End

    // Device And Ci Syncing Data - Start

    _generateDevicePropsData() {
        const deviceCustomFields = _.get(this._xDomeMetadata, "deviceCustomFields", []);
        const deviceProps = _.get(this._xDomeMetadata, "deviceProps", {});
        const xDomeDeviceProps = Object.keys(deviceProps);

        this._dataForCiAttrDropdown = [
            ...deviceCustomFields.map((field) => ({
                label: field.display,
                type: field.data_type,
                value: field.name,
                path: ["custom_fields", field.name],
            })),
            ...xDomeDeviceProps.map((key) => ({
                label: deviceProps[key].displayName,
                type: deviceProps[key].type,
                value: key,
                path: deviceProps[key].path,
            })),
        ];
        this._dataForCiAttrDropdownFiltered = [
            ...deviceCustomFields.map((field) => ({
                label: field.display,
                type: field.data_type,
                value: field.name,
                path: ["custom_fields", field.name],
            })),
            ...xDomeDeviceProps
                .filter((key) => !deviceProps[key].readOnly)
                .map((key) => ({
                    label: deviceProps[key].displayName,
                    type: deviceProps[key].type,
                    value: key,
                    path: deviceProps[key].path,
                })),
        ];

        // Sort the list alphabetically
        this._dataForCiAttrDropdown = this._dataForCiAttrDropdown.sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
        );

        this._dataForCiAttrDropdownFiltered = this._dataForCiAttrDropdownFiltered.sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
        );

        this._dataForCiAttrDropdownFiltered.push({label: "Other", value: "other"});

        this._dataForCiAttrDropdown.push({label: "Other", value: "other"});
    }

    _generateSitePropsData() {
        const siteProps = _.get(this._gluMetadata, "siteProps", {});
        this._dataForGluwareSiteDropdown = Object.entries(siteProps).map(([key, value]) => ({
            label: value.displayName,
            type: value.type,
            path: value.path,
            readonly: value.readOnly,
            value: key,
        }));

        const siteXDomeProps = _.get(this._xDomeMetadata, "siteProps", {});
        const siteXDomeCustomFields = _.get(this._xDomeMetadata, "siteCustomFields", []);

        const xDomeSiteProps = Object.keys(siteXDomeProps);

        this._dataForXDomeSiteDropdown = [
            ...siteXDomeCustomFields.map((field) => ({
                label: field.display,
                type: field.data_type,
                value: field.name,
                path: ["custom_fields", field.name],
            })),
            ...xDomeSiteProps.map((key) => ({
                label: siteXDomeProps[key].displayName,
                type: siteXDomeProps[key].type,
                value: key,
                path: siteXDomeProps[key].path,
            })),
        ];

        this._dataForXDomeSiteDropdownFiltered = [
            ...siteXDomeCustomFields.map((field) => ({
                label: field.display,
                type: field.data_type,
                value: field.name,
                path: ["custom_fields", field.name],
            })),
            ...xDomeSiteProps
                .filter((key) => !siteXDomeProps[key].readOnly)
                .map((key) => ({
                    label: siteXDomeProps[key].displayName,
                    type: siteXDomeProps[key].type,
                    value: key,
                    path: siteXDomeProps[key].path,
                })),
        ];

        this._dataForXDomeSiteDropdown = this._dataForXDomeSiteDropdown.sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
        );
        this._dataForXDomeSiteDropdownFiltered = this._dataForXDomeSiteDropdownFiltered.sort(
            (a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1)
        );

        this._dataForXDomeSiteDropdown.push({label: "Other", value: "other"});
        this._dataForXDomeSiteDropdownFiltered.push({label: "Other", value: "other"});
    }

    async _generateSlickDataForInstanceSyncing() {
        // TODO : First Grid
        this._dataForSlickTableSyncing = [];
        this._dataForSlickTableSyncing_XDomeToGlu = [];

        this._dataForSlickTableSiteSyncing = [];
        this._dataForSlickTableSiteSyncing_XdToGlu = [];

        const dataForSyncMapping = this._xdDataSyncConfigEntity.getDataSyncMappings() || {},
            deviceValueMaps = _.get(dataForSyncMapping, "valueMapping.device", []),
            siteValueMaps = _.get(dataForSyncMapping, "valueMapping.site", []);

        this._generateDataForDeviceSyncing(deviceValueMaps);
        this._generateDataForSiteSyncing(siteValueMaps);
    }

    _generateDataForDeviceSyncing(deviceValueMaps) {
        deviceValueMaps.forEach((item /*, idx */) => {
            const cmdbAttrObj = Array.isArray(this._dataForCiAttrDropdown)
                ? this._dataForCiAttrDropdown.find(
                      (i) => JSON.stringify(i.path) === JSON.stringify(item["xdPropPath"])
                  )
                : {};
            let cmdbAttr = !_.isEmpty(cmdbAttrObj)
                ? cmdbAttrObj.value
                : !_.isUndefined(item["xdExtendedPropPath"])
                ? "other_field." + item["xdExtendedPropPath"]
                : "";

            if (cmdbAttr === "other") {
                cmdbAttr = !_.isUndefined(item["xdExtendedPropPath"])
                    ? "other_field." + item["xdExtendedPropPath"]
                    : "";
            }

            if (item["syncDirection"] === "GW_TO_XD" || item["syncDirection"] === "glu_to_xd") {
                this._dataForSlickTableSyncing.push({
                    solutions: this._nodePropData,
                    deviceNodeTreeData: this._deviceNodeTreeData,
                    ciAttributesDropDownData: this._dataForCiAttrDropdownFiltered,
                    id: `id_${Math.random()}`,
                    gwPropPath: item["gwPropPath"],
                    gwPropType: item["gwPropType"],
                    cmdbAttr: cmdbAttr,
                    xdPropPath: item["xdPropPath"],
                    xdPropType: item["xdPropType"],
                    xdExtendedPropPath: item["xdExtendedPropPath"] || undefined,
                    type: "xDome",
                    syncDirection: item["syncDirection"],
                });
            }

            if (item["syncDirection"] === "XD_TO_GW") {
                this._dataForSlickTableSyncing_XDomeToGlu.push({
                    solutions: this._nodePropData,
                    deviceNodeTreeData: this._deviceNodeTreeData,
                    ciAttributesDropDownData: this._dataForCiAttrDropdown,
                    id: `id_${Math.random()}`,
                    gwPropPath: item["gwPropPath"],
                    gwPropType: item["gwPropType"],
                    cmdbAttr: cmdbAttr,
                    xdPropPath: item["xdPropPath"],
                    xdPropType: item["xdPropType"],
                    xdExtendedPropPath: item["xdExtendedPropPath"] || undefined,
                    type: "xDome",
                    syncDirection: item["syncDirection"],
                });
            }
        });

        // this.emit("RefreshDeviceAndCiSyncingGrid");
        this.emit("RefreshDeviceAndCiSyncingXDomeToGluGrid");
    }

    _generateDataForSiteSyncing(siteValueMaps) {
        siteValueMaps.forEach((item) => {
            const cmdbAttrObj = Array.isArray(this._dataForXDomeSiteDropdown)
                ? this._dataForXDomeSiteDropdown.find(
                      (i) => JSON.stringify(i.path) === JSON.stringify(item["xdPropPath"])
                  )
                : {};

            let cmdbAttr = !_.isEmpty(cmdbAttrObj)
                ? cmdbAttrObj.value
                : !_.isUndefined(item["xdExtendedPropPath"])
                ? "other_field." + item["xdExtendedPropPath"]
                : "";

            if (cmdbAttr === "other") {
                cmdbAttr = !_.isUndefined(item["xdExtendedPropPath"])
                    ? "other_field." + item["xdExtendedPropPath"]
                    : "";
            }

            const gwAttrObj = Array.isArray(this._dataForGluwareSiteDropdown)
                ? this._dataForGluwareSiteDropdown.find(
                      (i) => JSON.stringify(i.path) === JSON.stringify(item["gwPropPath"])
                  )
                : {};

            const gwAttr = !_.isEmpty(gwAttrObj) ? gwAttrObj.value : "";

            if (item["syncDirection"] === "GW_TO_XD" || item["syncDirection"] === "glu_to_xd") {
                this._dataForSlickTableSiteSyncing.push({
                    siteNodeDropDownData: this._dataForGluwareSiteDropdown,
                    ciAttributesDropDownData: this._dataForXDomeSiteDropdownFiltered,
                    id: `id_${Math.random()}`,
                    gwAttr: gwAttr,
                    gwPropPath: item["gwPropPath"],
                    gwPropType: item["gwPropType"],
                    cmdbAttr: cmdbAttr,
                    xdPropPath: item["xdPropPath"],
                    xdPropType: item["xdPropType"],
                    xdExtendedPropPath: item["xdExtendedPropPath"] || undefined,
                    type: "xDome_site",
                    syncDirection: item["syncDirection"],
                });
            }

            if (item["syncDirection"] === "XD_TO_GW") {
                this._dataForSlickTableSiteSyncing_XdToGlu.push({
                    siteNodeDropDownData: this._dataForGluwareSiteDropdown,
                    ciAttributesDropDownData: this._dataForXDomeSiteDropdown,
                    id: `id_${Math.random()}`,
                    gwAttr: gwAttr,
                    gwPropPath: item["gwPropPath"],
                    gwPropType: item["gwPropType"],
                    cmdbAttr: cmdbAttr,
                    xdPropPath: item["xdPropPath"],
                    xdPropType: item["xdPropType"],
                    xdExtendedPropPath: item["xdExtendedPropPath"] || undefined,
                    type: "xDome_site",
                    syncDirection: item["syncDirection"],
                });
            }
        });

        // this.emit("RefreshSiteAndCiSyncingGrid");
        this.emit("RefreshSiteAndCiSyncingXDomeToGluGrid");
    }

    // addNewD2CSyncingRule() {
    //     const idx = Math.random() * 100;
    //     const firstDevicePropNode = this.findFirstNode(this._nodePropData);
    //     const filteredCiDropDown = this._getFilteredCiAttributeList(
    //         _.get(firstDevicePropNode, "dataType", ""),
    //         true
    //     );

    //     if (!_.isEmpty(this._dataForCiAttrDropdownFiltered)) {
    //         this._dataForSlickTableSyncing.push({
    //             solutions: this._nodePropData,
    //             deviceNodeTreeData: this._deviceNodeTreeData,
    //             ciAttributesDropDownData: this._dataForCiAttrDropdownFiltered,
    //             id: `id_${idx}`,
    //             gwPropPath: _.get(firstDevicePropNode, "path", []),
    //             gwPropType: _.get(firstDevicePropNode, "dataType", ""),
    //             cmdbAttr: filteredCiDropDown ? filteredCiDropDown[0].value : "",
    //             xdPropType: this.findType(
    //                 filteredCiDropDown,
    //                 filteredCiDropDown ? filteredCiDropDown[0].value : ""
    //             ),
    //             xdPropPath: filteredCiDropDown[0].path,
    //             type: "xDome",
    //             syncDirection: "GW_TO_XD",
    //         });

    //         this.setDataSyncMappings();

    //         this.emit("RefreshDeviceAndCiSyncingGrid");
    //     } else {
    //         const errObj = {
    //             errorCode: "CI_ATTRIBUTES_ERR",
    //             errorMsg: `No CI Attributes exist for the selected node. You will not be able to add any Data Sync Mappings for the selected Node`,
    //             title: "Error",
    //         };
    //         return this._muf.displayError(errObj);
    //     }
    // }

    addNewD2CSyncingXDomeToGluRule() {
        const idx = Math.random() * 100;
        const firstDevicePropNode = this.findFirstNode(this._nodePropData);
        const filteredCiDropDown = this._getFilteredCiAttributeList(
            _.get(firstDevicePropNode, "dataType", "")
        );

        if (!_.isEmpty(this._dataForCiAttrDropdown)) {
            this._dataForSlickTableSyncing_XDomeToGlu.push({
                solutions: this._nodePropData,
                deviceNodeTreeData: this._deviceNodeTreeData,
                ciAttributesDropDownData: this._dataForCiAttrDropdown,
                id: `id_${idx}`,
                gwPropPath: _.get(firstDevicePropNode, "path", []),
                gwPropType: _.get(firstDevicePropNode, "dataType", ""),
                cmdbAttr:
                    filteredCiDropDown && filteredCiDropDown.length > 0
                        ? filteredCiDropDown[0].value
                        : "",
                xdPropType: this.findType(
                    filteredCiDropDown,
                    filteredCiDropDown && filteredCiDropDown.length > 0
                        ? filteredCiDropDown[0].value
                        : ""
                ),
                xdPropPath:
                    filteredCiDropDown && filteredCiDropDown.length > 0
                        ? filteredCiDropDown[0].path
                        : "",
                type: "xDome",
                syncDirection: "XD_TO_GW",
            });

            this.setDataSyncMappings();

            this.emit("RefreshDeviceAndCiSyncingXDomeToGluGrid");
        } else {
            const errObj = {
                errorCode: "CI_ATTRIBUTES_ERR",
                errorMsg: `No CI Attributes exist for the selected node. You will not be able to add any Data Sync Mappings for the selected Node`,
                title: "Error",
            };
            return this._muf.displayError(errObj);
        }
    }

    addNewS2CSyncingRule() {
        const idx = Math.random() * 100;
        const firstDevicePropNode = this._dataForGluwareSiteDropdown[0];

        const firstSiteXDomeProp = this._dataForXDomeSiteDropdownFiltered.find((item) => {
            if (this.typeComboAllowed(firstDevicePropNode.type, item.type)) {
                return item;
            }
            return false;
        });

        if (!_.isEmpty(this._dataForXDomeSiteDropdownFiltered)) {
            this._dataForSlickTableSiteSyncing.push({
                siteNodeDropDownData: this._dataForGluwareSiteDropdown,
                ciAttributesDropDownData: this._dataForXDomeSiteDropdownFiltered,
                id: `id_${idx}`,
                gwAttr: this._dataForGluwareSiteDropdown
                    ? this._dataForGluwareSiteDropdown[0].value
                    : "",
                gwPropPath: _.get(firstDevicePropNode, "path", []),
                gwPropType: _.get(firstDevicePropNode, "type", ""),
                cmdbAttr: firstSiteXDomeProp.value,
                xdPropType: firstSiteXDomeProp.type,
                xdPropPath: firstSiteXDomeProp.path,
                type: "xDome_site",
                syncDirection: "GW_TO_XD",
            });

            this.setDataSyncMappings();

            this.emit("RefreshSiteAndCiSyncingGrid");
        } else {
            const errObj = {
                errorCode: "CI_ATTRIBUTES_ERR",
                errorMsg: `No CI Attributes exist for the selected node. You will not be able to add any Data Sync Mappings for the selected Node`,
                title: "Error",
            };
            return this._muf.displayError(errObj);
        }
    }

    addNewS2CSyncingRule_XdToGlu() {
        const idx = Math.random() * 100;
        const firstDevicePropNode = this._dataForGluwareSiteDropdown[0];

        const firstSiteXDomeProp = this._dataForXDomeSiteDropdown.find((item) => {
            if (this.typeComboAllowed(firstDevicePropNode.type, item.type)) {
                return item;
            }
            return false;
        });

        if (!_.isEmpty(this._dataForXDomeSiteDropdown)) {
            this._dataForSlickTableSiteSyncing_XdToGlu.push({
                siteNodeDropDownData: this._dataForGluwareSiteDropdown,
                ciAttributesDropDownData: this._dataForXDomeSiteDropdown,
                id: `id_${idx}`,
                gwAttr: this._dataForGluwareSiteDropdown
                    ? this._dataForGluwareSiteDropdown[0].value
                    : "",
                gwPropPath: _.get(firstDevicePropNode, "path", []),
                gwPropType: _.get(firstDevicePropNode, "type", ""),
                cmdbAttr: firstSiteXDomeProp.value,
                xdPropType: firstSiteXDomeProp.type,
                xdPropPath: firstSiteXDomeProp.path,
                type: "xDome_site",
                syncDirection: "XD_TO_GW",
            });

            this.setDataSyncMappings();

            this.emit("RefreshSiteAndCiSyncingXDomeToGluGrid");
        } else {
            const errObj = {
                errorCode: "CI_ATTRIBUTES_ERR",
                errorMsg: `No CI Attributes exist for the selected node. You will not be able to add any Data Sync Mappings for the selected Node`,
                title: "Error",
            };
            return this._muf.displayError(errObj);
        }
    }

    addRemoveD2CLinkingIgnoreRule(item) {
        const dataLinkConfigList = this._xdDataSyncConfigEntity.getInstanceLinkingCriteria();

        if (!dataLinkConfigList[this._selectedClass]) {
            dataLinkConfigList[this._selectedClass] = [];
        }

        const idxActive = dataLinkConfigList[this._selectedClass].findIndex(
            (el) =>
                JSON.stringify(el.gwPropPath) === JSON.stringify(item.gwPropPath) &&
                el.cmdbAttr === item.cmdbAttr
        );

        if (idxActive > -1) {
            dataLinkConfigList[this._selectedClass].splice(idxActive, 1);
            if (
                _.isEmpty(dataLinkConfigList[this._selectedClass]) &&
                !_.isUndefined(dataLinkConfigList[this._selectedClass])
            ) {
                delete dataLinkConfigList[this._selectedClass];
            }
        } else {
            dataLinkConfigList[this._selectedClass].push({
                ignore: item.ignore,
                gwPropPath: item.gwPropPath,
                cmdbAttr: item.cmdbAttr,
                cmdbClass: this._selectedClass,
                ignorePlaceholder: true,
                sourceCmdbClass: item.sourceCmdbClass,
            });
        }

        //we  have to set the mappings
        this._xdDataSyncConfigEntity.setInstanceLinkingCriteria(dataLinkConfigList);

        this.emit("RefreshDeviceAndCiLinkingGrid");
    }

    _getFilteredCiAttributeList(gwPropType, filteredDropdown = false) {
        const dropDownFilteredData = [];
        if (_.isArray(this._dataForCiAttrDropdown)) {
            if (filteredDropdown) {
                this._dataForCiAttrDropdownFiltered.forEach((item) => {
                    if (this._shouldIncludeDeviceProp(item.type, gwPropType)) {
                        dropDownFilteredData.push(item);
                    }
                });
            } else {
                this._dataForCiAttrDropdown.forEach((item) => {
                    if (this._shouldIncludeDeviceProp(item.type, gwPropType)) {
                        dropDownFilteredData.push(item);
                    }
                });
            }
        }
        return dropDownFilteredData;
    }
    // Device And Ci Syncing Data - End

    /*******************************************/
    /* END SLICK DATA METHODS                  */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _triggerOrganizationDataChanged(orgData) {
        /**
         * @event OSManagementComponent#OrganizationDataChange
         * @param {Object} - new organization data.
         */
        this.emit("OrganizationDataChange", orgData);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    async _saveEntity() {
        const pendingPatches = this._xdDataSyncConfigEntity.pendingPatches || [];

        if (!this._xdDataSyncConfigEntity.isSynchronized()) {
            await this._xdDataSyncConfigEntity.create();
        } else if (this._xdDataSyncConfigEntity.isDirty()) {
            await this._xdDataSyncConfigEntity.update();
        }

        await Steve.controlAppNav.updateHomebarIoT();

        if (await this._runTestConnectionWork(this._serviceConnectionEntity, true)) {
            const shouldLoadMetadata = pendingPatches.some(
                (patch) =>
                    (patch.path === "/enabled" && patch.value === true) ||
                    patch.path === "/serviceConnectionId"
            );

            this._cmdbConnectionFailed = false;
            if (shouldLoadMetadata) {
                await this._loadMetaData();
            }

            this.emit("SavedWithGoodConnection");
        } else {
            this._cmdbConnectionFailed = true;
            this.emit("FailedCMDBConnect");
        }

        return Promise.resolve();
    }

    setSchedule(dateTime) {
        this._xdDataSyncConfigEntity.setSchedule(dateTime);
        this._xdDataSyncConfigEntity.setScheduleWorkInput({
            dataProvider: "claroty",
            dataProviderProduct: "xdome",
        });
    }

    setInstanceLinking() {
        // Retrieve existing linking criteria or initialize a new object
        const dataForLinkingCriteria =
            this._xdDataSyncConfigEntity.getInstanceLinkingCriteria() || {};

        // Capture the initial state of dataForLinkingCriteria[this._selectedClass]
        const oldSelectedLinkingCriteria = _.cloneDeep(
            dataForLinkingCriteria[this._selectedClass] || []
        );

        // Update the linking criteria for the selected class by removing specific properties from each item
        dataForLinkingCriteria[this._selectedClass] =
            this._dataForSlickTable.map((item) =>
                _.omit(item, [
                    "ciAttributesData",
                    "ciAttributesDropDownData",
                    "deviceNodeTreeData",
                    "id",
                    "solutions",
                    "cmdbAttr",
                    "type",
                ])
            ) || {};

        // Initialize error handling variables
        let errExists = false;
        this._isDnCLErrored = false;
        let err = "Errors were found while linking Gluware and xDome Device Properties: \n\n";
        this.errLinkingIds = [];

        // Validate each item in the data for linking
        this._dataForSlickTable.forEach((item) => {
            if (item.ignorePlaceholder !== true) {
                if (!this.typeComboAllowed(item.gwPropType, item.xdPropType)) {
                    err += `${item.gwPropType} and ${item.xdPropType} \n\n\n`;
                    errExists = true;
                    this._isDnCLErrored = true;
                    this.errLinkingIds.push(item.id);
                }
            }
        });

        // If there were any errors, display the error message and stop further execution
        if (errExists) {
            const errObj = {
                errorCode: "MAPPING_ERR",
                errorMsg: err,
                title: "Error",
            };
            // this.emit("ErrorInLinking", this.errLinkingIds);
            return this._muf.displayError(errObj);
        }

        // Remove placeholders linked to removed dataForLinkingCriteria
        const newSelectedLinkingCriteria = dataForLinkingCriteria[this._selectedClass];
        const removedCriteriaList = this._findDeletedRules(
            newSelectedLinkingCriteria,
            oldSelectedLinkingCriteria,
            ["gwPropPath", "cmdbAttr"]
        );
        removedCriteriaList.forEach((criteria) => {
            for (const [key, value] of Object.entries(dataForLinkingCriteria)) {
                dataForLinkingCriteria[key] = value.filter((item) => {
                    return !(
                        item.sourceCmdbClass === this._selectedClass &&
                        JSON.stringify(item.gwPropPath) === JSON.stringify(criteria[0]) &&
                        item.cmdbAttr === criteria[1]
                    );
                });
                if (
                    _.isEmpty(dataForLinkingCriteria[key]) &&
                    !_.isUndefined(dataForLinkingCriteria[key])
                ) {
                    delete dataForLinkingCriteria[key];
                }
            }
        });

        // If no errors, update the instance linking criteria with the new data
        this._xdDataSyncConfigEntity.setInstanceLinkingCriteria(dataForLinkingCriteria);
    }

    _findDeletedRules(newRules = [], oldRules = [], criteria = []) {
        const deletedAttributes = [];

        if (_.isArray(criteria) && criteria.length > 0) {
            for (const key in oldRules) {
                const oldRule = oldRules[key];
                const newRule = newRules[key] || [];

                const isDeleted = criteria.some((criterion) => {
                    return (
                        JSON.stringify(newRule[criterion]) !== JSON.stringify(oldRule[criterion])
                    );
                });

                if (isDeleted) {
                    deletedAttributes.push(
                        criteria.map((criterion) => {
                            return oldRule[criterion];
                        })
                    );
                }
            }
        }

        return deletedAttributes;
    }

    setDataSyncMappings() {
        // Retrieve existing syncing mappings or initialize a new object
        const dataSyncMappings = _.isEmpty(this._xdDataSyncConfigEntity.getDataSyncMappings())
            ? {valueMapping: {}}
            : this._xdDataSyncConfigEntity.getDataSyncMappings();
        if (!dataSyncMappings.valueMapping) {
            dataSyncMappings.valueMapping = {};
        }
        const valueMapping = dataSyncMappings.valueMapping;

        // Update the syncing mappings for the selected class by removing specific properties from each item
        valueMapping.device =
            [
                ...this._dataForSlickTableSyncing.map((item) =>
                    _.omit(item, [
                        "ciAttributesData",
                        "ciAttributesDropDownData",
                        "deviceNodeTreeData",
                        "id",
                        "solutions",
                        "cmdbAttr",
                        "type",
                    ])
                ),
                ...this._dataForSlickTableSyncing_XDomeToGlu.map((item) =>
                    _.omit(item, [
                        "ciAttributesData",
                        "ciAttributesDropDownData",
                        "deviceNodeTreeData",
                        "id",
                        "solutions",
                        "cmdbAttr",
                        "type",
                    ])
                ),
            ] || [];

        valueMapping.site =
            [
                ...this._dataForSlickTableSiteSyncing.map((item) =>
                    _.omit(item, [
                        "ciAttributesData",
                        "ciAttributesDropDownData",
                        "deviceNodeTreeData",
                        "id",
                        "solutions",
                        "cmdbAttr",
                        "gwAttr",
                        "type",
                        "siteNodeDropDownData",
                    ])
                ),
                ...this._dataForSlickTableSiteSyncing_XdToGlu.map((item) =>
                    _.omit(item, [
                        "ciAttributesData",
                        "ciAttributesDropDownData",
                        "deviceNodeTreeData",
                        "id",
                        "solutions",
                        "cmdbAttr",
                        "gwAttr",
                        "type",
                        "siteNodeDropDownData",
                    ])
                ),
            ] || [];

        // Initialize error handling variables for Device Syncing
        let errExists = false;
        this._isDnCSErrored = false;
        this.errSyncingIds = [];
        this.errSyncingIds_XDomeToGlu = [];

        // Validate each item in the data for linking
        this._dataForSlickTableSyncing.forEach((item) => {
            if (
                _.isUndefined(item.xdExtendedPropPath) &&
                !this.typeComboAllowed(item.gwPropType, item.xdPropType)
            ) {
                errExists = true;
                this._isDnCSErrored = true;
                this.errSyncingIds.push(item.id);
            } else if (
                !_.isUndefined(item.xdExtendedPropPath) &&
                _.trim(item.xdExtendedPropPath) === ""
            ) {
                errExists = true;
                this._isDnCSErrored = true;
                this.errSyncingIds.push(item.id);
            }
        });

        this._dataForSlickTableSyncing_XDomeToGlu.forEach((item) => {
            if (
                _.isUndefined(item.xdExtendedPropPath) &&
                !this.typeComboAllowed(item.gwPropType, item.xdPropType)
            ) {
                errExists = true;
                this._isDnCSErrored = true;
                this.errSyncingIds_XDomeToGlu.push(item.id);
            } else if (
                !_.isUndefined(item.xdExtendedPropPath) &&
                _.trim(item.xdExtendedPropPath) === ""
            ) {
                errExists = true;
                this._isDnCSErrored = true;
                this.errSyncingIds_XDomeToGlu.push(item.id);
            }
        });

        // If there were any errors, display the error message and stop further execution
        if (errExists) {
            if (!_.isEmpty(this.errSyncingIds)) {
                this.emit("ErrorInSyncing", this.errSyncingIds);
            } else if (!_.isEmpty(this.errSyncingIds_XDomeToGlu)) {
                this.emit("ErrorInSyncingXDomeToGlu", this.errSyncingIds_XDomeToGlu);
            }
        }

        // Initialize error handling variables for Site SYncing
        let errSiteExists = false;
        this._isSnCSErrored = false;
        this.errSiteSyncingIds = [];
        this.errSiteSyncingIds_XDomeToGlu = [];

        // Validate each item in the data for linking
        this._dataForSlickTableSiteSyncing.forEach((item) => {
            if (
                _.isUndefined(item.xdExtendedPropPath) &&
                !this.typeComboAllowed(item.gwPropType, item.xdPropType)
            ) {
                errSiteExists = true;
                this._isSnCSErrored = true;
                this.errSiteSyncingIds.push(item.id);
            } else if (
                !_.isUndefined(item.xdExtendedPropPath) &&
                _.trim(item.xdExtendedPropPath) === ""
            ) {
                errSiteExists = true;
                this._isSnCSErrored = true;
                this.errSiteSyncingIds.push(item.id);
            }
        });

        this._dataForSlickTableSiteSyncing_XdToGlu.forEach((item) => {
            if (
                _.isUndefined(item.xdExtendedPropPath) &&
                !this.typeComboAllowed(item.gwPropType, item.xdPropType)
            ) {
                errSiteExists = true;
                this._isSnCSErrored = true;
                this.errSiteSyncingIds_XDomeToGlu.push(item.id);
            } else if (
                !_.isUndefined(item.xdExtendedPropPath) &&
                _.trim(item.xdExtendedPropPath) === ""
            ) {
                errSiteExists = true;
                this._isSnCSErrored = true;
                this.errSiteSyncingIds_XDomeToGlu.push(item.id);
            }
        });

        // If there were any errors, display the error message and stop further execution
        if (errSiteExists) {
            if (!_.isEmpty(this.errSiteSyncingIds)) {
                this.emit("ErrorInSiteSyncing", this.errSiteSyncingIds);
            } else if (!_.isEmpty(this.errSiteSyncingIds_XDomeToGlu)) {
                this.emit("ErrorInSiteSyncingXdToGlu", this.errSiteSyncingIds_XDomeToGlu);
            }
        }
        // If no errors, update the instance syncing mappings with the new data
        this._xdDataSyncConfigEntity.setDataSyncMappings(dataSyncMappings);
    }

    setNamespaceMatchingCriteria() {
        const namespaceMatchingCriteria =
            this._dataForSlickTableMapping.map((item) =>
                _.omit(item, [
                    "classTreeData",
                    "classes",
                    "deviceNodeTreeData",
                    "id",
                    "deviceTypeData",
                    "vendorOsMap",
                    "cmdbAttr",
                    "type",
                ])
            ) || {};

        this._xdDataSyncConfigEntity.setNamespaceMatchingCriteria(namespaceMatchingCriteria);
    }

    async resetAllPatches() {
        await this._xdDataSyncConfigEntity.clearPendingPatches();
        this._isDnCSErrored = false;
        this._isSnCSErrored = false;
        this.emit("ResetAllValues");
    }

    async _retrieveCustomFieldsEntity() {
        try {
            const customFieldsEntity = await this._CustomFieldsEntityDef.retrieveCustomFields(
                this.activeOrgId
            );
            const inheritedFields = await customFieldsEntity.loadInheritedFields();

            if (_.isEmpty(customFieldsEntity.fields)) {
                this._customFieldsWithInherited = {};
            } else {
                this._customFieldsWithInherited = customFieldsEntity.fields;
            }

            if (!_.isEmpty(inheritedFields)) {
                Object.assign(this._customFieldsWithInherited, inheritedFields);
            }

            await customFieldsEntity.shutdown();

            return this._customFieldsWithInherited;
        } catch (error) {
            this._logger.error(
                `Failed to retrieve custom fields for organization ID: ${this.activeOrgId}`,
                {error}
            );
            throw error; // Rethrow error to handle it upstream if needed
        }
    }

    /*******************************************/
    /* END ENTITY METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    deleteInstanceSyncingCriteria(id, type) {
        if (type === "GW_TO_XD") {
            this._dataForSlickTableSyncing = this._dataForSlickTableSyncing.filter(
                (item) => item.id !== id
            );
        } else {
            this._dataForSlickTableSyncing_XDomeToGlu =
                this._dataForSlickTableSyncing_XDomeToGlu.filter((item) => item.id !== id);
        }
    }

    deleteSiteInstanceSyncingCriteria(id, type) {
        if (type === "GW_TO_XD") {
            this._dataForSlickTableSiteSyncing = this._dataForSlickTableSiteSyncing.filter(
                (item) => item.id !== id
            );
        } else {
            this._dataForSlickTableSiteSyncing_XdToGlu =
                this._dataForSlickTableSiteSyncing_XdToGlu.filter((item) => item.id !== id);
        }
    }

    loadOrganizationData() {
        return this._organizationService.getById({id: this.activeOrgId}).then((orgData) => {
            this._organizationData = orgData;
            this._triggerOrganizationDataChanged(orgData);
            return orgData;
        });
    }

    async _loadSlickDropdownsData(selectedTreeNode) {
        try {
            await this._loadDevicePropsData();
            await this._loadMappingAttributes(selectedTreeNode);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    async _loadDevicePropsData() {
        try {
            if (_.isEmpty(this._nodePropData)) {
                this._nodePropData = await this._xdDataSyncConfigEntity.loadSyncableDeviceProps();
                this._deviceNodeTreeData = this._generateTreeDataForNodeProps(this._nodePropData);
            }
            return Promise.resolve();
        } catch (err) {
            this._logger.warn(err, "xDomeViewComponent._loadDevicePropsData() error.");
        }
    }

    async _loadMappingAttributes(selectedNode = ["cmdb_ci"]) {
        try {
            let classQuery = {};
            if (_.isArray(selectedNode)) {
                selectedNode.forEach((node) => Object.assign(classQuery, {[node]: true}));
            } else if (_.isString(selectedNode)) {
                classQuery = {[selectedNode]: true};
            }

            // Loads only child and all needed parent attributes
            this._classAttributes = await this._xdDataSyncConfigEntity.loadMappableAttributes({
                classNames: classQuery,
                includeDerived: true,
            });

            let attributes = {};
            for (const node of Object.values(this._classAttributes)) {
                // No spread operator for now
                Object.assign(attributes, node.attributes || {});
            }
            this._dataForCiAttrDropdown = _.keys(attributes).map((item) => {
                return {
                    label: attributes[item].displayName,
                    value: item,
                    type: attributes[item].type,
                };
            });

            this._dataForCiAttrDropdown.sort((a, b) => a.label.localeCompare(b.label));

            this._dataForCiAttrDropdown.push({label: "Other", value: "other"});
        } catch (err) {
            this._logger.warn(err, "xDomeViewComponent._loadMappingAttributes() error.");
        }
    }

    _sortObjectByValues(obj) {
        const entries = Object.entries(obj);
        entries.sort((a, b) => a[1].toLowerCase().localeCompare(b[1].toLowerCase()));
        return Object.fromEntries(entries);
    }

    _loadDeviceTypes() {
        try {
            // Check if metadata exists and try to extract vendor map
            if (this._metaData) {
                let deviceTypeBaseMap = _.get(this._metaData, "gluware.typeBases", {});

                if (!_.isEmpty(deviceTypeBaseMap)) {
                    deviceTypeBaseMap = this._sortObjectByValues(deviceTypeBaseMap);
                    this._deviceTypeBaseMap = deviceTypeBaseMap;
                    return deviceTypeBaseMap;
                }
            }

            // Fallback: Attempt to load device type map from external source
            let deviceTypeBaseMap = this._DeviceEntityDef.getDetectedRawTypeBaseLabels();
            if (deviceTypeBaseMap && typeof deviceTypeBaseMap === "object") {
                deviceTypeBaseMap = this._sortObjectByValues(deviceTypeBaseMap);
                this._deviceTypeBaseMap = deviceTypeBaseMap;
                return deviceTypeBaseMap;
            }

            throw new Error("Failed to retrieve device type base map");
        } catch (error) {
            console.error("Error loading device type map:", error.message);
            return null; // Return a safe fallback value
        }
    }

    async _loadVendorOsMap() {
        try {
            const vendorOsMap = await this._ValueMapEntity.loadVendorOsMapEntity(this.activeOrgId);
            if (vendorOsMap) {
                this._vendorOsMap = vendorOsMap.getValueMap();
                return vendorOsMap;
            } else {
                this.emit("NoVendorMap");
                this._noVendorMap = true;
            }
        } catch (error) {
            console.error("Error loading Vendor OS map:", error);
        }
    }

    async _loadVendorMap() {
        try {
            // Check if metadata exists and try to extract vendor map
            if (this._metaData) {
                const vendorMap = _.get(this._metaData, "gluware.vendors", {});

                if (!_.isEmpty(vendorMap)) {
                    // Clone the vendor map to avoid side effects and remove 'ALL'
                    let sanitizedVendorMap = this._sortObjectByValues(Object.assign({}, vendorMap));
                    sanitizedVendorMap.ALL = undefined;
                    this._vendorMap = sanitizedVendorMap;
                    return sanitizedVendorMap;
                }
            }

            // Fallback: Attempt to load vendor map from external source
            let vendorMap = await this._DeviceEntityDef.loadAndMergeVendorMap(this.activeOrgId);
            if (vendorMap && typeof vendorMap === "object") {
                vendorMap = this._sortObjectByValues(vendorMap);
                this._vendorMap = vendorMap;
                return vendorMap;
            }

            throw new Error("Failed to retrieve vendor map");
        } catch (error) {
            console.error("Error loading vendor map:", error.message);
            return null; // Return a safe fallback value
        }
    }

    async _loadOsMap() {
        try {
            let osMap = await this._DeviceEntityDef.loadAndMergeOsMap(this.activeOrgId);
            if (osMap) {
                osMap = this._sortObjectByValues(osMap);
                this._osMap = osMap;
                return osMap;
            }
        } catch (error) {
            console.error("Error loading OS map:", error);
        }
    }

    async _loadSitePathMap() {
        try {
            const sites = await this._SiteEntityDef.loadSites(
                this.activeOrgId,
                {},
                {
                    id: true,
                    path: true,
                    name: true,
                    locationType: true,
                },
                {
                    path: 1,
                }
            );
            if (sites) {
                const mergedMap = Object.assign({}, ...sites.map(({id, path}) => ({[id]: path})));
                this._sitePathMap = this._sortObjectByValues(mergedMap);
                return this._sitePathMap;
            }
        } catch (error) {
            console.error("Error loading Site Path map:", error);
        }
    }

    async _loadSkuList() {
        try {
            // Check if metadata exists and try to extract SKU list
            if (this._metaData) {
                const vendorSkuList =
                    (this._metaData && this._metaData.gluware && this._metaData.gluware.skus) || {};

                if (!_.isEmpty(vendorSkuList)) {
                    const skuList = [];

                    for (const vendor in vendorSkuList) {
                        if (
                            typeof vendorSkuList[vendor] === "object" &&
                            !Array.isArray(vendorSkuList[vendor])
                        ) {
                            const vendorSkus = Object.keys(vendorSkuList[vendor]);
                            for (let i = 0; i < vendorSkus.length; i++) {
                                skuList.push(vendorSkus[i]);
                            }
                        }
                    }

                    this._skuList = skuList.sort((a, b) =>
                        a.toLowerCase().localeCompare(b.toLowerCase())
                    );
                    return skuList;
                }
            }

            // Fallback: Attempt to load SKU list from external source
            const skuList = await this._DeviceEntityDef.loadDeviceDistinctValues(
                this.activeOrgId,
                "detectedSku",
                {}
            );
            if (skuList) {
                this._skuList = skuList.sort((a, b) =>
                    a.toLowerCase().localeCompare(b.toLowerCase())
                );
                return skuList;
            }

            throw new Error("Failed to retrieve SKU list");
        } catch (error) {
            console.error("Error loading SKU List:", error);
        }
    }

    async _loadMetaData() {
        try {
            const workInput = {
                dataProvider: "claroty",
                dataProviderProduct: "xdome",
            };

            const workResults = await this._xdDataSyncConfigEntity.runMetaDataWork(workInput);

            const errorMsg = _.get(workResults, "summary.errorMsg", "");

            if (errorMsg) {
                throw new Error(`Metadata work failed: ${errorMsg}`);
            }

            this._metaData = _.get(workResults, "summary.metadata", {});

            this._xDomeMetadata = _.get(workResults, "summary.metadata.xdome", {});
            this._gluMetadata = _.get(workResults, "summary.metadata.gluware", {});
            this._supportedMappings = _.get(this._gluMetadata, "supportedMappings", {});
            this._xDomeToGluMappings = _.get(this._supportedMappings, "instanceToDevice", {});

            this._nodePropData = _.get(this._gluMetadata, "deviceExplorables");
            this._deviceNodeTreeData = this._generateTreeDataForNodeProps(this._nodePropData);

            this._generateDevicePropsData();
            this._generateSitePropsData();

            return this._metaData;
        } catch (error) {
            this._logger.warn("Error loading Metadata:", error);
            return {};
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START RUNWORK METHODS METHODS           */
    /*******************************************/

    async downloadLastSyncReport() {
        let latestWork = await this._xdDataSyncConfigEntity.loadRelatedWorks(
            {},
            {},
            {_id: -1},
            {skip: 0, limit: 1}
        );
        if (latestWork.length > 0) {
            latestWork = latestWork[0];
        }

        const workReport = (await latestWork?.loadRelatedReports())?.[0] ?? {};
        const workLog = (await latestWork?.loadRelatedLog())?.log ?? {};

        const hasReport = !_.isEmpty(workReport);
        const hasLog = !_.isEmpty(workLog);

        if (!hasReport && !hasLog) {
            const err = {
                errorCode: "WORK_ERROR",
                errorMsg:
                    "No report or log available. Please initiate a data sync to generate them.",
                errorTitle: "Error In Downloading Last Sync Data",
            };
            await this._muf.displayError(err);
            return;
        }

        const fileConfig = {};
        const date = workReport?.updatedOn || workReport?.createdOn || new Date();

        if (hasReport) {
            const csvReport = workReport.report;
            const reportFileName = this._renderer._fileNamer({
                solution: "SS",
                general: "CMDBIntegration-LastReport",
                specific: this._organizationData.name,
                date,
            });

            fileConfig[reportFileName + ".csv"] = {
                type: "text/csv",
                content: csvReport,
            };
        }

        if (hasLog) {
            const logFileName = this._renderer._fileNamer({
                solution: "SS",
                general: "CMDBIntegration-LastLog",
                specific: this._organizationData.name,
                date,
            });

            fileConfig[logFileName + ".json"] = {
                type: "application/json",
                content: JSON.stringify(workLog, null, "\t"),
            };

            const logLines = workLog.map((entry) => `${entry.time} ${entry.msg}`).join("\n");
            fileConfig[logFileName + ".txt"] = {
                type: "text/plain",
                content: logLines,
            };
        }

        const zipFileName = this._renderer._fileNamer({
            solution: "SS",
            general: "CMDBIntegration-LastSync",
            specific: this._organizationData.name,
            date,
            suffix: "zip",
        });

        this._fileFactory.saveStringsAsZip(fileConfig, zipFileName);

        if (!hasReport) {
            const err = {
                errorCode: "PARTIAL_DOWNLOAD",
                errorMsg:
                    "No report available. Only log downloaded. Please run a new data sync to generate a complete report.",
                errorTitle: "Partial Download",
            };
            await this._muf.displayError(err);
        } else if (!hasLog) {
            const err = {
                errorCode: "PARTIAL_DOWNLOAD",
                errorMsg:
                    "No log available. Only report downloaded. Please run a new data sync to generate a complete log.",
                errorTitle: "Partial Download",
            };
            await this._muf.displayError(err);
        }
    }

    // TODO : Check if a schedule exists. Will circle back to this
    // async _loadSchedule() {
    //     const relatedSchedule = await this._xdDataSyncConfigEntity.loadRelatedSchedule();

    //     console.log(relatedSchedule);
    // }

    async syncWork(preview = false) {
        const workInput = {
            dataProviderProduct: "xdome",
            dataProvider: "claroty",
            syncOptions: {
                preview,
                isEditable: true,
            },
        };
        const schedName = `Claroty xDome ${preview ? "Preview " : ""}Sync from Settings`;
        const schedDescription = preview
            ? "Preview data synchronization between Claroty xDome and Gluware"
            : "Synchronize data between Claroty xDome and Gluware";
        const workTarget = {
            entityType: "Device",
            category: "entity",
            method: "query",
            query: {},
        };

        // Schedule 2 minutes ahead as buffer
        const now = new Date();
        now.setMinutes(now.getMinutes() + 2);
        const schedule = {
            type: "one",
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            on: {
                year: now.getFullYear(),
                month: now.getMonth(),
                date: now.getDate(),
                hour: now.getHours(),
                minute: now.getMinutes(),
            },
        };

        return this._DeviceEntityDef
            .scheduleDataSyncWork(
                this.activeOrgId,
                schedName,
                schedDescription,
                schedule,
                workTarget,
                workInput
            )
            .then((resultEntity) => {
                return this._muf
                    .displayConfirmation(
                        "The sync has been scheduled and will start in about 2 minutes. You can monitor progress and download logs in the Schedules solution.",
                        {
                            title: `${preview ? "Preview" : "Run"} Syncing Now`,
                            confirmDetails: {
                                text: "View in Schedules",
                                type: "additive",
                            },
                            rejectDetails: {
                                text: "Close",
                                type: "text",
                            },
                        }
                    )
                    .then(() => {
                        return this._router.goTo({
                            app: "GameConsole",
                            s: "GameCenter",
                            sln: "WorkTriggers",
                            exp: "WorkTriggers",
                            rd: {
                                openEditor: true,
                                selection: [resultEntity.getId()],
                            },
                        });
                    });
            })
            .catch((err) => {
                if (err.userRejected) {
                    return this._muf.hideDisplayed();
                }
                this._logger.warn(err, "xDomeViewComponent.SyncWork() error.");
                return this._muf.displayError({
                    errorCode: "ERROR",
                    errorMsg: err.message,
                });
            });
    }

    async _runTestConnectionWork(connEntity, silentCheck = false) {
        this._muf.hideDisplayed();

        try {
            if (!connEntity) {
                const {serviceConnectionId, pristineProperties} = this._xdDataSyncConfigEntity;
                if (serviceConnectionId === _.get(pristineProperties, "serviceConnectionId")) {
                    this.emit("ConnectionFailed");
                }
                return false;
            }

            // Active org, not the connection's owning org: an inherited connection's tenant is an
            // ancestor the caller may not be authorized to launch work against.
            const workTarget = {
                category: "entity",
                entityType: "ServiceConnection",
                method: "single",
                id: connEntity.id,
            };
            const res = await this._ServiceConnectionEntityDef.runTestConnectionWork(
                this.activeOrgId,
                workTarget
            );

            if (_.get(res, "summary.errorMsg", false)) {
                this._logger.warn(
                    _.get(res, "summary.errorMsg", false),
                    "xDomeViewComponent._runTestConnectionWork() error."
                );
                if (!silentCheck) {
                    const userMsg = [
                        "Failed to connect to xDome CMDB.",
                        "New changes will take effect after Save.\r\n",
                    ].join("\r\n\r\n");
                    const errObj = {
                        errorCode: "CONNECTION_ERROR",
                        errorMsg: userMsg,
                        title: "Connection Failed",
                    };
                    this._muf.displayError(errObj);
                }

                /**
                 * This condition checks if we test and connection has not changed but still the connection succeeded or failed.
                 * It will change the connection status
                 */
                if (
                    this._xdDataSyncConfigEntity.serviceConnectionId ===
                    this._xdDataSyncConfigEntity.pristineProperties.serviceConnectionId
                ) {
                    this._cmdbConnectionFailed = true;
                    this.emit("ConnectionFailed");
                }
                return false;
            } else {
                if (!silentCheck) {
                    const userMsg = [
                        "Successfully connected to xDome CMDB.",
                        "You will need to Save before all tabs will refresh.\r\n",
                    ].join("\r\n\r\n");
                    await this._muf.displayNotification(userMsg, {
                        notDismissable: true,
                        title: "Connection Successful",
                        confirmDetails: {text: "OK"},
                    });
                }
                /**
                 * This condition checks if we test and connection has not changed but still the connection succeeded or failed.
                 * It will change the connection status
                 */
                if (
                    this._xdDataSyncConfigEntity.serviceConnectionId ===
                    this._xdDataSyncConfigEntity.pristineProperties.serviceConnectionId
                ) {
                    this._cmdbConnectionFailed = false;
                    this.emit("ConnectionSuccessful");
                    this.emit("SavedWithGoodConnection");
                }
                return true;
            }
        } catch (err) {
            this._logger.warn(err, "xDomeViewComponent._runTestConnectionWork() error.");
        }
    }

    /*******************************************/
    /* END RUNWORK METHODS MEtHODS             */
    /* START HELPER METHODS                    */
    /*******************************************/

    typeComboAllowed(gwPropType, xdPropType) {
        return (
            this._xDomeToGluMappings[xdPropType] &&
            this._xDomeToGluMappings[xdPropType].includes(gwPropType)
        );
    }

    findFirstNode(obj) {
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                if (_.isObject(obj[key]) && obj[key] !== null) {
                    if ("dataType" in obj[key] && "heading" in obj[key] && "path" in obj[key]) {
                        return obj[key];
                    } else {
                        const result = this.findFirstNode(obj[key]);
                        if (result) {
                            return result;
                        }
                    }
                }
            }
        }
    }

    // Find type for the CmdbCiAttribute
    findType(entries, valueToFind) {
        for (const entry of entries) {
            if (entry.value === valueToFind) {
                return entry.type;
            }
        }
        return null;
    }

    // Find path for the deviceProps
    findPathForDeviceProps(input, obj) {
        for (const key in obj) {
            if (
                key === "path" &&
                Array.isArray(obj[key]) &&
                obj[key].join(".") === input.join(".")
            ) {
                return obj.dataType; // Return the dataType if path matches
            } else if (typeof obj[key] === "object") {
                const result = this.findPathForDeviceProps(input, obj[key]);
                if (result) {
                    return result;
                }
            }
        }
        return null; // If not found, return null
    }

    // Device Node Prop Tree Data generation - Start
    _generateTreeDataForNodeProps(explorables) {
        if (_.isEmpty(explorables)) {
            return [];
        }
        const nodes = [];
        for (let key in explorables) {
            if (!explorables[key].systemOwned) {
                nodes.push(this._generateTreeNodeForNodeProps(key, explorables[key]));
            }
        }
        const prunedNodes = [];
        const pruneEmptyChildren = (array, prunedArray) => {
            for (const item of array) {
                if (typeof item === "object") {
                    let _item = _.cloneDeep(item);
                    if (_item.children.length > 0) {
                        const prunedChildren = [];
                        pruneEmptyChildren(_item.children, prunedChildren);
                        _item.children = prunedChildren;
                        prunedArray.push(_item);
                    }
                } else {
                    prunedArray.push(item);
                }
            }
        };
        pruneEmptyChildren(nodes, prunedNodes);
        return prunedNodes;
    }

    _generateTreeNodeForNodeProps(name, nodeData) {
        if (!nodeData.path) {
            const node = {};
            node.text = name;
            node.children = [];
            for (let key in nodeData) {
                if (!nodeData[key].systemOwned) {
                    node.children.push(this._generateTreeNodeForNodeProps(key, nodeData[key]));
                }
            }
            return node;
        }
        return name;
    }

    _shouldIncludeNode(nodeData, dataType) {
        const typeRules = {
            string: ["string", "GUID", "ip_addr"],
            number: ["integer", "decimal", "float"],
            boolean: ["boolean"],
            unixTime: ["glide_date_time", "glide_date"],
        };

        if (dataType in typeRules) {
            return typeRules[dataType].includes(nodeData.dataType);
        }

        return false;
    }

    // Device Node Prop Tree Data generation - End

    /**
     * _shouldIncludeDeviceProp
     * @param {*} ciDataType
     * @param {*} nodeDataType
     * @returns
     */
    _shouldIncludeDeviceProp(ciDataType, nodeDataType) {
        return (
            this._xDomeToGluMappings[ciDataType] &&
            this._xDomeToGluMappings[ciDataType].includes(nodeDataType)
        );
    }

    /*******************************************/
    /* END HELPER MEtHODS                      */
    /*******************************************/
}

module.exports = xDomeViewComponent;
